/**
 * Animal Health Repositioning — Screening Rules Page (Jul 2026)
 * ==============================================================
 * Standalone workspace for defining rules that translate the user's
 * plain-English intent into structured filters used to screen the
 * global human pharma/biotech asset universe for animal-health
 * repositioning candidates.
 *
 * Entirely separate from Viatris and Modulus workbenches — the
 * backend collections (`ah_screening_definitions`, `ah_screening_templates`)
 * are dedicated to this module.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, ShieldCheck,
  Save, Copy, Trash2, Sparkles, RefreshCw, PlusCircle, X,
  ChevronDown, ChevronRight, TrendingDown, PlayCircle, ExternalLink,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api/animal-health';

// Feb 2026 — Templates + Prompt Mode UI un-hidden after a live demo
// showed the BD team had no self-serve way to recover from an emptied
// definition (had to run a console command). The Templates picker lets
// admins re-apply the seed template in one click.
const SHOW_ADVANCED_CONFIG = true;

// ─────────────────────────────────────────────────────────────
// Helpers to convert between regex ↔ chip lists (visual editor).
// ─────────────────────────────────────────────────────────────
function regexToList(rx) {
  if (!rx) return [];
  const stripped = String(rx).replace(/^\^?\(|\)\$?$/g, '').replace(/^\(|\)$/g, '');
  return stripped
    .split('|')
    .map(s => s.replace(/\\s\*/g, ' ').trim())
    .filter(Boolean);
}
function listToRegex(list) {
  const cleaned = list.map(x => String(x).trim()).filter(Boolean);
  return cleaned.length ? cleaned.join('|') : '';
}

// Default empty definition matching the backend Pydantic shape.
// v23 simplified shape — Optional and Priority buckets kept in schema
// for back-compat but never populated by the frontend.
const EMPTY_DEFINITION = {
  include: {
    stages_regex: '',
    therapeutic_areas_regex: '',
    therapeutic_area_diseases: {},   // v23 — {category: [diseases]}
    therapeutic_area_categories: [],
    modalities: [],
    match_secondary_indication: true,
  },
  optional: {
    stages_regex: '', therapeutic_areas_regex: '',
    therapeutic_area_categories: [], modalities: [],
  },
  priority: { stages: [], therapeutic_areas: [] },
  exclude: {
    stages_regex: '',                // v23 — dedicated exclude stages
    therapeutic_areas: [], modalities: [], asset_types: [],
    licensing_status: [], keywords: [],
  },
  version: '',
};

// ─────────────────────────────────────────────────────────────
// Chip tag input — reused across every rule row.
// ─────────────────────────────────────────────────────────────
const TagChips = ({ values, onChange, placeholder, testId, tone = 'slate' }) => {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  const toneCls = {
    slate:   'bg-slate-100 border-slate-200 text-slate-700',
    green:   'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    rose:    'bg-rose-50 border-rose-200 text-rose-800',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-800',
  }[tone] || 'bg-slate-100 border-slate-200 text-slate-700';
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1 mb-1 min-h-[24px]">
        {values.length === 0 && (
          <span className="text-[11px] text-slate-400 italic">No values yet</span>
        )}
        {values.map((v, i) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] ${toneCls}`}
          >
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              className="text-slate-400 hover:text-rose-600"
              data-testid={`${testId}-remove-${i}`}
              aria-label={`Remove ${v}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          className="flex-1 text-[11px] px-2 py-1 rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={commit}
          disabled={!draft.trim()}
          className="p-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          data-testid={`${testId}-add`}
          title="Add value (or press Enter)"
        >
          <PlusCircle className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// A single rule row (Include / Optional / Exclude / Priority) — pure
// chip editor, no radio: caller decides which rows appear per dimension.
const RuleRow = ({ label, tone, icon: Icon, values, onChange, placeholder, testId }) => (
  <div className="border-l-2 pl-3 py-2 border-slate-200" data-testid={testId}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className={`w-3.5 h-3.5 ${
        tone === 'green' ? 'text-emerald-600'
        : tone === 'amber' ? 'text-amber-600'
        : tone === 'rose' ? 'text-rose-600'
        : tone === 'indigo' ? 'text-indigo-600'
        : 'text-slate-500'
      }`} />
      <span className="text-[11.5px] font-semibold text-slate-800">{label}</span>
    </div>
    <TagChips
      values={values}
      onChange={onChange}
      placeholder={placeholder}
      testId={`${testId}-tags`}
      tone={tone}
    />
  </div>
);

// A single tile in the funnel banner. Compact by default; expanding
// reveals the top dropped values inline below the banner.
const FunnelBlock = ({
  label, value, removed, pctRemoved,
  tone = 'slate', highlight = false,
  expandable = false, expanded = false, onToggle,
  subLabel, testId, onClick, clickable = false,
}) => {
  const toneCls = {
    slate:   'bg-white border-slate-200 text-slate-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  }[tone] || 'bg-white border-slate-200 text-slate-800';
  const interactive = expandable || clickable;
  return (
    <div
      className={`rounded border ${toneCls} px-2 py-1.5 min-w-[110px] flex-1 relative ${highlight ? 'ring-2 ring-emerald-300' : ''} ${interactive ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
      onClick={expandable ? onToggle : (clickable ? onClick : undefined)}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      data-testid={testId}
    >
      <div className="flex items-center gap-1">
        <span className="text-[9.5px] uppercase tracking-wide font-semibold text-slate-500 truncate flex-1" title={label}>
          {label}
        </span>
        {expandable && (
          expanded ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />
        )}
        {clickable && !expandable && (
          <ExternalLink className="w-3 h-3 text-emerald-600" />
        )}
      </div>
      <div className="text-[16px] font-bold leading-tight tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {typeof removed === 'number' && removed > 0 && (
        <div className="text-[9.5px] text-rose-700">
          −{removed.toLocaleString()} ({pctRemoved}%)
        </div>
      )}
      {!removed && subLabel && (
        <div className="text-[9.5px] text-emerald-700">{subLabel}</div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Therapeutic-Area accordion — v23 primary editing surface.
// Each category is a collapsible row with a chip-editable disease list.
// On first expand of a category with an empty list, we seed from the
// ontology defaults so the user has a starting point they can edit.
// ─────────────────────────────────────────────────────────────
const TADiseasesAccordion = ({ categories, value, onChange }) => {
  const [openKey, setOpenKey] = useState(null);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const toggle = (name) => setOpenKey(k => k === name ? null : name);
  const setDiseases = (name, list) => onChange({ ...value, [name]: list });

  // Feb 2026 · v26 — Custom therapeutic areas.
  // Any key present in `value` that isn't part of the server ontology
  // is treated as a user-added custom category. Rendered inline after
  // the built-in categories so BD leads can group new deal areas
  // (e.g. "Rare Disease Programs", "Longevity") without waiting for
  // an ontology update.
  const builtInNames = new Set((categories || []).map(c => c.name));
  const customEntries = Object.keys(value || {}).filter(k => !builtInNames.has(k));
  const builtInCats = categories || [];

  const removeCustom = (name) => {
    const next = { ...(value || {}) };
    delete next[name];
    onChange(next);
    if (openKey === name) setOpenKey(null);
  };

  const commitCustom = () => {
    const raw = (customName || '').trim();
    if (!raw) return;
    // Prevent duplicating a built-in category name (case-insensitive).
    const clash = builtInNames.has(raw) ||
      Object.keys(value || {}).some(k => k.toLowerCase() === raw.toLowerCase());
    if (clash) {
      setCustomName('');
      setAddingCustom(false);
      // Just open the existing row so the user sees where to add diseases.
      setOpenKey(raw);
      return;
    }
    onChange({ ...(value || {}), [raw]: [] });
    setOpenKey(raw);
    setCustomName('');
    setAddingCustom(false);
  };

  const renderRow = (cat, isCustom = false, isLast = false) => {
    const diseases = value[cat.name] || [];
    const open = openKey === cat.name;
    const active = diseases.length > 0;
    const hasDefaults = !isCustom && (cat.terms || []).length > 0;
    const key = cat.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
    return (
      <div
        key={cat.name}
        className={`border-t border-slate-200 ${active ? 'bg-emerald-50/30' : (isCustom ? 'bg-amber-50/30' : 'bg-white')}`}
        data-testid={`ah-ta-accordion-row-${key}`}
      >
        <button
          type="button"
          onClick={() => toggle(cat.name)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
          data-testid={`ah-ta-accordion-header-${key}`}
        >
          {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
          <span className={`text-[12px] font-semibold flex-1 ${active ? 'text-emerald-800' : 'text-slate-800'}`}>
            {cat.name}
            {isCustom && (
              <span className="ml-2 text-[9.5px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm uppercase tracking-wide">
                Custom
              </span>
            )}
          </span>
          <span className="text-[10.5px] text-slate-500">
            {active
              ? `${diseases.length} disease${diseases.length === 1 ? '' : 's'}`
              : 'click to add diseases'}
          </span>
          {active && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setDiseases(cat.name, []); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setDiseases(cat.name, []); } }}
              className="ml-1 text-rose-400 hover:text-rose-600 cursor-pointer"
              title="Clear all diseases in this category"
              data-testid={`ah-ta-accordion-clear-${key}`}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          {isCustom && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); removeCustom(cat.name); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); removeCustom(cat.name); } }}
              className="ml-2 text-slate-400 hover:text-rose-600 cursor-pointer text-[10.5px] font-medium"
              title="Remove this custom category"
              data-testid={`ah-ta-accordion-remove-custom-${key}`}
            >
              Remove
            </span>
          )}
        </button>
        {open && (
          <div className="px-3 pb-3 pt-1 bg-white border-t border-slate-100">
            <TagChips
              values={diseases}
              onChange={list => setDiseases(cat.name, list)}
              placeholder="add a disease (Enter to commit)"
              testId={`ah-ta-accordion-diseases-${key}`}
              tone="green"
            />
            {hasDefaults && (
              <button
                type="button"
                onClick={() => setDiseases(cat.name, [...new Set([...diseases, ...cat.terms])])}
                className="mt-1 text-[10.5px] text-indigo-600 hover:text-indigo-800"
                data-testid={`ah-ta-accordion-restore-${key}`}
              >
                + Restore ontology defaults ({cat.terms.length})
              </button>
            )}
            {isCustom && (
              <p className="mt-1 text-[10.5px] text-slate-500">
                Custom category · lives inside this screening only. The AI searches primary + secondary indication + pipeline description + MoA + asset summary — same as built-in categories.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="ah-ta-accordion">
      {/* First row's top border is provided by the container */}
      {builtInCats.map((cat) => renderRow(cat, false))}
      {customEntries.map((name) => renderRow({ name, terms: [] }, true))}
      <div className="border-t border-slate-200 bg-slate-50/60 px-3 py-2">
        {addingCustom ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              autoFocus
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitCustom(); }
                if (e.key === 'Escape') { setCustomName(''); setAddingCustom(false); }
              }}
              placeholder="Custom category name (e.g. Rare Disease Programs)"
              className="flex-1 text-[12px] px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              data-testid="ah-ta-add-custom-input"
            />
            <button
              type="button"
              onClick={commitCustom}
              disabled={!customName.trim()}
              className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ah-ta-add-custom-confirm"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setCustomName(''); setAddingCustom(false); }}
              className="text-[11px] text-slate-600 hover:text-slate-800 px-1.5 py-1.5"
              data-testid="ah-ta-add-custom-cancel"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingCustom(true)}
            className="text-[11px] font-medium text-emerald-700 hover:text-emerald-900 flex items-center gap-1"
            data-testid="ah-ta-add-custom-btn"
          >
            <span className="text-[13px] leading-none">+</span> Add custom category
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function AnimalHealthScreeningPage() {
  const [defn, setDefn] = useState(EMPTY_DEFINITION);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseSummary, setParseSummary] = useState('');
  const [templates, setTemplates] = useState([]);
  const [ontology, setOntology] = useState([]);  // [{name, terms[]}, …]
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // v26 — "Diagnose Asset" panel: paste an asset name or company name
  // and see whether the current rules would include it, and if not, why.
  const [diagnoseQuery, setDiagnoseQuery] = useState('');
  const [diagnoseResult, setDiagnoseResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  // Live funnel — recomputed 700ms after any rule change, and manually
  // via the "Recompute" button. Never runs on the first render (waits
  // for `defn` to be populated from GET first).
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelExpanded, setFunnelExpanded] = useState({});  // step_key → bool
  // Ontology expand-on-click modal.
  const [ontologyOpen, setOntologyOpen] = useState(null);  // category name or null
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsDesc, setSaveAsDesc] = useState('');
  const navigate = useNavigate();

  // Chip-view of regex fields — kept in sync with `defn` so the user
  // edits chips and we translate to regex on save.
  const chipView = useMemo(() => ({
    include_stages:   regexToList(defn.include.stages_regex),
    include_tas:      regexToList(defn.include.therapeutic_areas_regex),
    optional_stages:  regexToList(defn.optional.stages_regex),
    optional_tas:     regexToList(defn.optional.therapeutic_areas_regex),
  }), [defn]);

  const updateRegex = (bucket, key, list) => {
    setDefn(d => ({
      ...d,
      [bucket]: { ...d[bucket], [key]: listToRegex(list) },
    }));
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, t, o] = await Promise.all([
        axios.get(`${API}/screening-definition`, { withCredentials: true }),
        axios.get(`${API}/screening-templates`, { withCredentials: true }),
        axios.get(`${API}/ontology`, { withCredentials: true }),
      ]);
      // Fill any missing keys with defaults so the UI never sees undefined.
      const cleaned = {
        ...EMPTY_DEFINITION,
        ...d.data,
        include:  { ...EMPTY_DEFINITION.include,  ...(d.data.include  || {}) },
        optional: { ...EMPTY_DEFINITION.optional, ...(d.data.optional || {}) },
        priority: { ...EMPTY_DEFINITION.priority, ...(d.data.priority || {}) },
        exclude:  { ...EMPTY_DEFINITION.exclude,  ...(d.data.exclude  || {}) },
      };
      setDefn(cleaned);
      setTemplates(t.data?.templates || []);
      setOntology(o.data?.therapeutic_areas || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load screening rules');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const parseFromPrompt = async () => {
    if (!prompt.trim()) { toast.error('Type your screening intent first'); return; }
    setParsing(true);
    try {
      const res = await axios.post(
        `${API}/screening-definition/parse-from-prompt`,
        { prompt: prompt.trim() },
        { withCredentials: true, timeout: 60000 },
      );
      const p = res.data?.parsed;
      if (!p) { toast.error('LLM returned no structure'); return; }
      const nextDefn = {
        include:  { ...EMPTY_DEFINITION.include,  ...(p.include  || {}) },
        optional: { ...EMPTY_DEFINITION.optional, ...(p.optional || {}) },
        priority: { ...EMPTY_DEFINITION.priority, ...(p.priority || {}) },
        exclude:  { ...EMPTY_DEFINITION.exclude,  ...(p.exclude  || {}) },
        version:  `Prompt-parsed · ${new Date().toISOString().slice(0, 10)}`,
      };
      setDefn(nextDefn);
      const counts = [
        `${regexToList(nextDefn.include.stages_regex).length} stage-include`,
        `${Object.values(nextDefn.include.therapeutic_area_diseases || {}).reduce((a,l) => a+l.length, 0)} TA-diseases (across ${Object.values(nextDefn.include.therapeutic_area_diseases || {}).filter(l => l.length).length} categories)`,
      ];
      setParseSummary(`AI extracted → ${counts.join(' · ')}`);
      toast.success('Rules parsed — review below, then Save');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/screening-definition`, defn,
        { withCredentials: true },
      );
      toast.success('Screening rules saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async (templateId) => {
    if (!templateId) return;
    try {
      const res = await axios.post(
        `${API}/screening-templates/${templateId}/apply`,
        {}, { withCredentials: true },
      );
      const d = res.data?.definition;
      if (d) {
        setDefn({
          ...EMPTY_DEFINITION,
          ...d,
          include:  { ...EMPTY_DEFINITION.include,  ...(d.include  || {}) },
          optional: { ...EMPTY_DEFINITION.optional, ...(d.optional || {}) },
          priority: { ...EMPTY_DEFINITION.priority, ...(d.priority || {}) },
          exclude:  { ...EMPTY_DEFINITION.exclude,  ...(d.exclude  || {}) },
        });
        toast.success('Template loaded — you can edit before saving');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load template');
    }
  };

  // Feb 2026 · One-click self-heal for the "definition got wiped" state.
  // Finds the seed template, applies it into this workspace, and saves it
  // in a single pass. Used by the empty-definition guard banner rendered
  // above the funnel section (see below).
  const restoreBaseTemplate = async () => {
    try {
      const list = await axios.get(`${API}/screening-templates`, { withCredentials: true });
      const seed = (list.data?.templates || []).find(t => t.is_seed);
      if (!seed) {
        toast.error('Base template missing on server — contact support.');
        return;
      }
      await applyTemplate(seed.id);
      // Persist immediately — user should never have to remember to
      // hit "Save rules" after clicking a recovery banner.
      await axios.put(`${API}/screening-definition`, defn, { withCredentials: true });
      const wd = await axios.get(`${API}/screening-definition?workspace_id=default`, { withCredentials: true });
      setDefn({
        ...EMPTY_DEFINITION,
        ...wd.data,
        include:  { ...EMPTY_DEFINITION.include,  ...(wd.data?.include  || {}) },
        optional: { ...EMPTY_DEFINITION.optional, ...(wd.data?.optional || {}) },
        priority: { ...EMPTY_DEFINITION.priority, ...(wd.data?.priority || {}) },
        exclude:  { ...EMPTY_DEFINITION.exclude,  ...(wd.data?.exclude  || {}) },
      });
      toast.success('Base template restored and saved. Redirecting to cohort…');
      setTimeout(() => { window.location.href = '/animal-health/cohort'; }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const saveAsTemplate = async () => {
    if (!saveAsName.trim()) { toast.error('Give the template a name'); return; }
    try {
      const res = await axios.post(
        `${API}/screening-templates`,
        {
          name: saveAsName.trim(),
          description: saveAsDesc.trim(),
          definition: defn,
        },
        { withCredentials: true },
      );
      toast.success(`Template "${res.data?.name}" saved`);
      setSaveAsOpen(false);
      setSaveAsName('');
      setSaveAsDesc('');
      // Refresh template list
      const t = await axios.get(`${API}/screening-templates`, { withCredentials: true });
      setTemplates(t.data?.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save-as failed');
    }
  };

  const deleteTemplate = async (t) => {
    if (t.is_seed) { toast.error('Base template cannot be deleted'); return; }
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/screening-templates/${t.id}`, { withCredentials: true });
      toast.success('Template deleted');
      setTemplates(templates.filter(x => x.id !== t.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await axios.post(
        `${API}/screening-definition/preview-count`,
        { definition: defn },
        { withCredentials: true, timeout: 60000 },
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  // v26 — Diagnose why a specific asset is / isn't in the eligible cohort.
  const runDiagnose = async () => {
    const q = (diagnoseQuery || '').trim();
    if (!q) { toast.error('Enter an asset or company name'); return; }
    setDiagnosing(true);
    setDiagnoseResult(null);
    try {
      const res = await axios.post(
        `${API}/screening-definition/diagnose-asset`,
        { definition: defn, query: q },
        { withCredentials: true, timeout: 60000 },
      );
      setDiagnoseResult(res.data);
      if (!res.data.count) toast.info('No assets matched that name');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnose failed');
    } finally {
      setDiagnosing(false);
    }
  };

  const [funnelError, setFunnelError] = useState('');

  // ── Live funnel — computes Global → Stage → TA → Eligible ──
  const computeFunnel = async (defnArg = null) => {
    setFunnelLoading(true);
    setFunnelError('');
    try {
      const res = await axios.post(
        `${API}/screening-definition/funnel`,
        { definition: defnArg || defn, top_dropped_n: 8 },
        { withCredentials: true, timeout: 60000 },
      );
      setFunnel(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.message
        || 'Funnel failed';
      setFunnelError(String(msg));
      // Silent fail on debounced auto-runs — but surface for manual.
      if (!defnArg) toast.error(msg);
    } finally {
      setFunnelLoading(false);
    }
  };

  // Debounced auto-recompute — 800ms after last rule change.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => { computeFunnel(defn); }, 800);
    return () => clearTimeout(t);
  }, [defn, loading]);

  // ─── Render ────────────────────────────────────────────────
  return (
    <div className="p-6 overflow-y-auto min-h-screen bg-slate-50" data-testid="ah-screening-page">
      <div className="max-w-5xl mx-auto">
          {/* Header */}
          <header className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h1 className="text-2xl font-semibold text-slate-900">
                Animal Health Repositioning — Screening Rules
              </h1>
            </div>
            <p className="text-[13px] text-slate-600 max-w-3xl">
              Screens the <strong>global human pharma / biotech asset universe</strong> for
              assets with potential to be repositioned into animal health. Describe the
              intent in plain English, review the parsed rules, then save (or save as a
              reusable template).
            </p>
          </header>

          {/* Feb 2026 · Empty-definition guard.
              If the saved workspace has no stages AND no TA categories,
              the funnel would show 14,606 → 14,606 → 14,606 (all assets
              pass because there are no include filters), which then
              crashes the Cohort page trying to render everything. This
              banner offers a one-click restore to the seed template so
              admins never have to fall back to console commands. */}
          {(() => {
            const inc = defn?.include || {};
            const noStages = !inc.stages_regex && !(inc.stages || []).length;
            const noTAs = Object.keys(inc.therapeutic_area_diseases || {}).length === 0
                        && !inc.therapeutic_areas_regex;
            const isEmpty = noStages && noTAs;
            if (!isEmpty || loading) return null;
            return (
              <section
                className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4"
                data-testid="ah-empty-def-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl">⚠️</div>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-amber-900">
                      Screening rules are empty
                    </div>
                    <p className="text-[12px] text-amber-800 mt-0.5">
                      No stages and no therapeutic areas are selected — the funnel is passing
                      all {(funnel?.scanned || '14,606').toLocaleString?.() || '14,606'} assets
                      through, which will crash the Cohort page. Restore the base template to
                      get back to a working state (all 13 TA categories + Phase 1–Marketed stages).
                    </p>
                  </div>
                  <Button
                    onClick={restoreBaseTemplate}
                    className="h-8 text-[12px] bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    data-testid="ah-restore-base-template-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Restore base template
                  </Button>
                </div>
              </section>
            );
          })()}

          {/* ── Live funnel banner ────────────────────────────
              Shows Global Universe → Development Stage → Therapeutic Areas → Eligible Cohort
              with per-step % dropped + top removed values. Auto-recomputes
              800ms after any rule change. */}
          <section
            className="bg-gradient-to-r from-indigo-50 to-white border border-indigo-200 rounded-lg p-3 mb-4"
            data-testid="ah-funnel-banner"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-indigo-700" />
              <span className="text-[12px] font-semibold text-indigo-900">
                Screening Funnel {funnelLoading && (
                  <span className="text-slate-500 font-normal">
                    <Loader2 className="inline w-3 h-3 animate-spin ml-1" /> recomputing…
                  </span>
                )}
              </span>
              <Button
                variant="ghost" size="sm"
                className="h-6 text-[10.5px] ml-auto"
                onClick={() => computeFunnel()}
                disabled={funnelLoading || loading}
                data-testid="ah-funnel-recompute"
              >
                <RefreshCw className="w-3 h-3 mr-1" />Recompute
              </Button>
            </div>
            {funnelError && (
              <div
                className="mb-2 px-3 py-2 rounded border border-rose-300 bg-rose-50 text-[11.5px] text-rose-800 flex items-start gap-2"
                data-testid="ah-funnel-error"
              >
                <span className="font-semibold">Funnel failed:</span>
                <span className="break-words">{funnelError}</span>
              </div>
            )}
            {funnel ? (
              <>
                {/* Horizontal step display */}
                <div className="flex items-stretch gap-1 overflow-x-auto">
                  <FunnelBlock
                    label="Global universe"
                    value={funnel.scanned}
                    tone="slate"
                    testId="ah-funnel-global"
                  />
                  {funnel.steps.map(s => (
                    <React.Fragment key={s.key}>
                      <div className="flex items-center text-slate-400 px-0.5 text-[10px]">→</div>
                      <FunnelBlock
                        label={s.label}
                        value={s.after}
                        removed={s.removed}
                        pctRemoved={s.pct_removed}
                        tone={s.removed === 0 ? 'slate' : 'amber'}
                        expandable={s.removed > 0}
                        expanded={!!funnelExpanded[s.key]}
                        onToggle={() => setFunnelExpanded(e => ({ ...e, [s.key]: !e[s.key] }))}
                        testId={`ah-funnel-step-${s.key}`}
                      />
                    </React.Fragment>
                  ))}
                  <div className="flex items-center text-slate-400 px-0.5 text-[10px]">→</div>
                  <FunnelBlock
                    label="Eligible cohort"
                    value={funnel.eligible}
                    pctRemoved={funnel.final_pct}
                    tone="emerald"
                    highlight
                    clickable
                    onClick={() => navigate('/animal-health/cohort')}
                    testId="ah-funnel-eligible"
                    subLabel={`${funnel.final_pct}% of universe · click to open`}
                  />
                </div>
                {/* Drop reasons — expand any step to reveal */}
                {funnel.steps.some(s => funnelExpanded[s.key] && s.top_dropped_values?.length) && (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {funnel.steps.filter(s => funnelExpanded[s.key] && s.top_dropped_values?.length).map(s => (
                      <div
                        key={s.key}
                        className="bg-white border border-slate-200 rounded p-2"
                        data-testid={`ah-funnel-reasons-${s.key}`}
                      >
                        <div className="text-[10.5px] font-semibold text-slate-700 mb-1">
                          Why removed at {s.label} ({s.removed} assets)
                        </div>
                        {s.top_dropped_values.map((tv, i) => (
                          <div key={i} className="flex justify-between text-[10.5px] py-0.5 border-b border-slate-100 last:border-0">
                            <span className="text-slate-700 truncate mr-2" title={tv.value}>{tv.value}</span>
                            <span className="text-slate-500 font-semibold shrink-0">{tv.count}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-[9.5px] text-slate-500 mt-1.5">
                  Universe: full live asset database. Click any step to expand and see the top removed values.
                </div>
              </>
            ) : (
              <div className="text-[11px] text-slate-500 italic py-2">
                Funnel will populate once rules are loaded — or click Recompute.
              </div>
            )}
          </section>

          {/* Templates bar (v25 — hidden behind SHOW_ADVANCED_CONFIG flag) */}
          {SHOW_ADVANCED_CONFIG && (
          <section className="bg-white border border-slate-200 rounded-lg p-3 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-2">
                Templates
              </span>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger
                  className="h-8 text-[12px] w-[320px] bg-white"
                  data-testid="ah-template-select"
                >
                  <SelectValue placeholder="Load a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 && (
                    <SelectItem value="_none" disabled>No templates yet</SelectItem>
                  )}
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id} data-testid={`ah-template-opt-${t.id}`}>
                      {t.name}{t.is_seed ? ' · seed' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-[12px]"
                onClick={() => setSaveAsOpen(true)}
                data-testid="ah-save-as-template-btn"
              >
                <Copy className="w-3 h-3 mr-1" /> Save current as template
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-[12px]"
                onClick={loadAll}
                data-testid="ah-refresh-btn"
                title="Reload from server"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
              <div className="ml-auto flex flex-wrap items-center gap-1">
                {templates.filter(t => !t.is_seed).map(t => (
                  <button
                    key={t.id}
                    onClick={() => deleteTemplate(t)}
                    className="inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-200"
                    data-testid={`ah-template-delete-${t.id}`}
                    title={`Delete template: ${t.name}`}
                  >
                    <Trash2 className="w-2.5 h-2.5" /> {t.name}
                  </button>
                ))}
              </div>
            </div>
          </section>
          )}

          {/* Prompt Mode (v25 — hidden behind SHOW_ADVANCED_CONFIG flag) */}
          {SHOW_ADVANCED_CONFIG && (
          <section className="bg-white border border-indigo-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <span className="text-[12px] font-semibold text-indigo-800">
                Prompt Mode — describe your screening intent
              </span>
            </div>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={5}
              placeholder='e.g. "Include Phase 1 to Approved plus in-species. Therapeutic areas: Ophthalmology (dry eye, keratoconjunctivitis sicca), Dermatology (atopic dermatitis, pruritus), Oncology (lymphoma, mast cell tumour). Match on primary or secondary indication."'
              className="w-full text-[12px] px-2 py-1.5 rounded border border-slate-300 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
              disabled={parsing}
              data-testid="ah-prompt-textarea"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                className="h-7 text-[11px] bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={parseFromPrompt}
                disabled={parsing}
                data-testid="ah-parse-btn"
              >
                {parsing
                  ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Parsing…</>
                  : <><Sparkles className="w-3 h-3 mr-1" />Parse with AI</>}
              </Button>
              <span className="text-[10px] text-slate-500">
                Parses the intent into structured rules below — you review + tweak, then Save.
              </span>
            </div>
            {parseSummary && (
              <div
                className="mt-2 text-[10.5px] text-indigo-900 bg-indigo-50 border border-indigo-100 rounded px-2 py-1"
                data-testid="ah-parse-summary"
              >
                {parseSummary}
              </div>
            )}
          </section>
          )}

          {/* Structured rules editor */}
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-[13px] py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading current rules…
            </div>
          ) : (
            <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-5">
              {/* Version label */}
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                  Version label
                </label>
                <input
                  type="text"
                  value={defn.version || ''}
                  onChange={e => setDefn(d => ({ ...d, version: e.target.value }))}
                  placeholder="e.g. Animal Health — Aug 2026 baseline"
                  className="w-full mt-1 px-2 py-1 text-[12px] rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                  data-testid="ah-version-input"
                />
              </div>

              {/* v25 — Development Stage (INCLUDE ONLY) */}
              <div>
                <h2 className="text-[13px] font-semibold text-slate-800 mb-2">
                  Development Stage
                  <span className="ml-2 text-[10.5px] font-normal text-slate-500">
                    Keep assets matching one or more selected stages. All other stages are excluded automatically.
                  </span>
                </h2>
                <div className="grid grid-cols-1 gap-2">
                  <RuleRow
                    label="Include (must match)" tone="green" icon={ShieldCheck}
                    values={chipView.include_stages}
                    onChange={list => updateRegex('include', 'stages_regex', list)}
                    placeholder="Phase 1 / Phase 2 / Phase 3 / Approved / Registered / Marketed / In-species"
                    testId="ah-stage-include"
                  />
                </div>
              </div>

              {/* v25 — Therapeutic Area accordion (editable diseases per category) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-[13px] font-semibold text-slate-800">
                    Therapeutic Areas
                    <span className="ml-2 text-[10.5px] font-normal text-slate-500">
                      Click a category to expand and add diseases. The AI searches primary + secondary indication + pipeline description + MoA + asset summary.
                    </span>
                  </h2>
                  {Object.values(defn.include.therapeutic_area_diseases || {}).some(l => (l || []).length > 0) && (
                    <button
                      type="button"
                      onClick={() => {
                        // Feb 2026 · Guarded — previously a single click nuked
                        // every disease from every TA and there was no undo,
                        // which mid-demo left the funnel with 14,606 → 14,606.
                        const total = Object.values(defn.include.therapeutic_area_diseases || {})
                          .reduce((n, l) => n + (l || []).length, 0);
                        if (!window.confirm(
                          `Clear all ${total} disease chips from every TA category?\n\n` +
                          `This wipes every disease across all 13 TA categories at once. ` +
                          `You can restore the base template afterwards, but individual custom edits will be lost.\n\n` +
                          `Continue?`
                        )) return;
                        setDefn(d => ({
                          ...d,
                          include: {
                            ...d.include,
                            therapeutic_area_diseases: Object.fromEntries(
                              Object.keys(d.include.therapeutic_area_diseases || {}).map(k => [k, []])
                            ),
                          },
                        }));
                      }}
                      className="text-[10.5px] text-rose-600 hover:text-rose-800 font-medium whitespace-nowrap ml-3"
                      data-testid="ah-ta-clear-all"
                    >
                      Clear all TA diseases
                    </button>
                  )}
                </div>
                <TADiseasesAccordion
                  categories={ontology}
                  value={defn.include.therapeutic_area_diseases || {}}
                  onChange={(next) => setDefn(d => ({
                    ...d,
                    include: { ...d.include, therapeutic_area_diseases: next },
                  }))}
                />
              </div>
            </section>
          )}

          {/* Sticky action bar */}
          <div className="mt-4 flex flex-wrap items-center gap-2 sticky bottom-2 bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
            <Button
              onClick={save}
              disabled={saving || loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="ah-save-btn"
            >
              {saving
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
                : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save rules</>}
            </Button>
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={previewing || loading}
              data-testid="ah-preview-btn"
            >
              {previewing
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Previewing…</>
                : 'Preview universe count'}
            </Button>
            {preview && (
              <span className="text-[12px] text-slate-700 ml-2" data-testid="ah-preview-summary">
                <strong className="text-emerald-700">{preview.eligible}</strong>
                {' '}of <strong>{preview.scanned}</strong> assets pass
                {preview.scan_limit && preview.scanned >= preview.scan_limit && (
                  <em className="text-slate-500"> · scan capped at {preview.scan_limit}</em>
                )}
                {Object.keys(preview.dropped_by_stage || {}).length > 0 && (
                  <span className="text-slate-500 ml-2">
                    · dropped: {Object.entries(preview.dropped_by_stage)
                      .map(([k, v]) => `${k} ${v}`).join(' · ')}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* v26 — Diagnose Asset panel */}
          <div className="mt-3 bg-white border border-slate-200 rounded-lg p-3" data-testid="ah-diagnose-panel">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[12px] font-semibold text-slate-700">Diagnose an asset</span>
              <span className="text-[11px] text-slate-500">
                Paste an asset or company name (e.g. &quot;Encuragen&quot;) to see whether the current rules include it — and if not, why.
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={diagnoseQuery}
                onChange={(e) => setDiagnoseQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runDiagnose()}
                placeholder="Asset name or company name…"
                className="text-[12px] px-2 py-1.5 border border-slate-300 rounded min-w-[280px]"
                data-testid="ah-diagnose-input"
              />
              <Button
                onClick={runDiagnose}
                disabled={diagnosing || loading}
                size="sm"
                variant="outline"
                data-testid="ah-diagnose-btn"
              >
                {diagnosing
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Checking…</>
                  : 'Check'}
              </Button>
            </div>
            {diagnoseResult && diagnoseResult.matches?.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="ah-diagnose-results">
                {diagnoseResult.matches.slice(0, 10).map((m) => (
                  <div
                    key={m.id}
                    className={`text-[12px] px-2 py-1.5 rounded border ${m.included ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${m.included ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
                        {m.included ? 'INCLUDED' : `EXCLUDED · ${m.excluded_stage || 'unknown'}`}
                      </span>
                      <strong className="text-slate-900">{m.asset_name || '—'}</strong>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-700">{m.company_name || '—'}</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-600">{m.phase || 'no stage'}</span>
                      {m.primary_indication && (
                        <>
                          <span className="text-slate-500">·</span>
                          <span className="text-slate-700 italic">{m.primary_indication}</span>
                        </>
                      )}
                    </div>
                    {!m.included && m.excluded_reason && (
                      <div className="mt-1 text-slate-700">
                        <span className="text-slate-500">Why: </span>{m.excluded_reason}
                      </div>
                    )}
                  </div>
                ))}
                {diagnoseResult.matches.length > 10 && (
                  <div className="text-[11px] text-slate-500">
                    Showing 10 of {diagnoseResult.matches.length} matches — refine the query for a tighter list.
                  </div>
                )}
              </div>
            )}
            {diagnoseResult && diagnoseResult.matches?.length === 0 && (
              <div className="mt-2 text-[12px] text-slate-500">
                No assets found matching &quot;{diagnoseResult.query}&quot;. Try a company name, or check the master asset record exists.
              </div>
            )}
          </div>

        {/* Ontology child-term explorer modal */}
        <Dialog open={!!ontologyOpen} onOpenChange={(o) => !o && setOntologyOpen(null)}>
          <DialogContent className="max-w-lg" data-testid="ah-ontology-modal">
            <DialogHeader>
              <DialogTitle>
                {ontologyOpen?.name}
                <span className="ml-2 text-[11px] font-normal text-slate-500">
                  {ontologyOpen?.terms?.length || 0} expanded terms
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[420px] overflow-y-auto">
              <p className="text-[11px] text-slate-500 mb-2">
                Selecting this category enables all the following terms in the whitelist regex. Matching runs case-insensitively across primary + secondary indication + pipeline description + MoA + asset summary.
              </p>
              <div className="grid grid-cols-2 gap-1">
                {(ontologyOpen?.terms || []).map((t, i) => (
                  <div
                    key={i}
                    className="text-[11px] px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-800"
                    data-testid={`ah-ontology-term-${i}`}
                  >
                    {t}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOntologyOpen(null)} data-testid="ah-ontology-close">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save-as-template dialog */}
        <Dialog open={saveAsOpen} onOpenChange={setSaveAsOpen}>
          <DialogContent className="max-w-md" data-testid="ah-save-as-dialog">
            <DialogHeader>
              <DialogTitle>Save current rules as template</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Name</label>
                <input
                  type="text"
                  value={saveAsName}
                  onChange={e => setSaveAsName(e.target.value)}
                  placeholder="e.g. Animal Health — Dermatology only"
                  className="w-full mt-1 px-2 py-1.5 text-[13px] rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                  data-testid="ah-save-as-name"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Description (optional)</label>
                <textarea
                  value={saveAsDesc}
                  onChange={e => setSaveAsDesc(e.target.value)}
                  rows={3}
                  placeholder="Short note explaining when to use this template"
                  className="w-full mt-1 px-2 py-1.5 text-[12px] rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                  data-testid="ah-save-as-desc"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveAsOpen(false)} data-testid="ah-save-as-cancel">
                Cancel
              </Button>
              <Button
                onClick={saveAsTemplate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="ah-save-as-confirm"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
